import { useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useEditor, EditorContent, Extension, type Editor } from '@tiptap/react';
import { Markdown, type MarkdownManager } from '@tiptap/markdown';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import clsx from 'clsx';
import { Flex, Text } from '@nula/core-ui';
import type { DiffLabels } from '@/lib/documents/markdown-preview-context';
import { readOnlyContentExtensions } from './read-only-extensions';
import {
  addedTextRanges,
  removedTextRuns,
  textOffsetToPosition,
  textOffsetToBlockPosition,
  textRangesToPositions,
  BLOCK_SEPARATOR,
  type RemovedRun,
} from './added-text-ranges';
import styles from './markdown-section-preview.module.css';

const addedWordsKey = new PluginKey<DecorationSet>('markdownPreviewAddedWords');

const AddedWords = Extension.create({
  name: 'markdownPreviewAddedWords',
  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: addedWordsKey,
        state: {
          init: () => DecorationSet.empty,
          apply: (tr, set) => {
            const next = tr.getMeta(addedWordsKey) as DecorationSet | undefined;
            return next ?? set.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations: (state) => addedWordsKey.getState(state),
        },
      }),
    ];
  },
});

function parseBaseline(editor: Editor, markdown: MarkdownManager, baseline: string): ProseMirrorNode | null {
  try {
    return editor.schema.nodeFromJSON(markdown.parse(baseline));
  } catch (error) {
    Sentry.captureException(error);
    console.error('section preview baseline failed to parse', error);
    return null;
  }
}

interface PreviewDiff {
  decorations: DecorationSet;
  formattingOnly: boolean;
}

const NO_DIFF: PreviewDiff = { decorations: DecorationSet.empty, formattingOnly: false };

function hiddenText(text: string): HTMLElement {
  const span = document.createElement('span');
  span.className = styles.visuallyHidden;
  span.textContent = text;
  return span;
}

function removedWidget(run: RemovedRun, labels: DiffLabels | undefined): () => HTMLElement {
  return () => {
    const del = document.createElement('del');
    del.className = run.block ? styles.removedBlock : styles.removed;
    if (labels) del.append(hiddenText(`${labels.removed} `));
    del.append(run.text);
    if (labels && !run.block) del.append(hiddenText(' '));
    return del;
  };
}

function previewDiff(editor: Editor, baseline: string | undefined, labels: DiffLabels | undefined): PreviewDiff {
  const { doc } = editor.state;
  if (baseline === undefined || !editor.markdown) return NO_DIFF;
  const before = parseBaseline(editor, editor.markdown, baseline);
  if (!before) return NO_DIFF;
  const beforeText = before.textBetween(0, before.content.size, BLOCK_SEPARATOR);
  const afterText = doc.textBetween(0, doc.content.size, BLOCK_SEPARATOR);
  const added = addedTextRanges(beforeText, afterText);
  const removed = removedTextRuns(beforeText, afterText);
  const decorations: Decoration[] = [];

  for (const range of added) {
    const positions = textRangesToPositions(doc, [range]);
    positions.forEach(({ from, to }) => {
      decorations.push(Decoration.inline(from, to, { nodeName: 'ins', class: styles.added }));
    });
    if (labels && positions.length > 0) {
      const text = `${labels.added} `;
      decorations.push(Decoration.widget(positions[0].from, () => hiddenText(text), {
        side: -1,
        ignoreSelection: true,
        key: `added-${range.start}-${range.end}`,
      }));
    }
  }

  for (const run of removed) {
    const pos = run.block ? textOffsetToBlockPosition(doc, run.at) : textOffsetToPosition(doc, run.at);
    if (pos === null) continue;
    decorations.push(Decoration.widget(pos, removedWidget(run, labels), {
      side: -2,
      ignoreSelection: true,
      key: `removed-${run.block ? 'block' : 'inline'}-${run.at}-${run.text}`,
    }));
  }

  return {
    decorations: DecorationSet.create(doc, decorations),
    formattingOnly:
      baseline !== '' && doc.textContent !== '' && added.length === 0 && removed.length === 0,
  };
}

export interface MarkdownSectionPreviewProps {
  markdown: string;
  baseline?: string;
  className?: string;
  labels?: DiffLabels;
}

export function MarkdownSectionPreview({ markdown, baseline, className, labels }: MarkdownSectionPreviewProps) {
  const editor = useEditor({
    editable: false,
    immediatelyRender: false,
    editorProps: { attributes: { class: 'nula-editor-root' } },
    extensions: [...readOnlyContentExtensions(), Markdown, AddedWords],
    content: markdown,
    contentType: 'markdown',
  });

  const added = labels?.added;
  const removed = labels?.removed;
  const formattingChanged = labels?.formattingChanged;
  const diffLabels = useMemo<DiffLabels | undefined>(
    () =>
      added === undefined || removed === undefined || formattingChanged === undefined
        ? undefined
        : { added, removed, formattingChanged },
    [added, removed, formattingChanged],
  );
  const [formattingOnly, setFormattingOnly] = useState(false);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.commands.setContent(markdown, { contentType: 'markdown', emitUpdate: false });
    const diff = previewDiff(editor, baseline, diffLabels);
    editor.view.dispatch(editor.state.tr.setMeta(addedWordsKey, diff.decorations));
    setFormattingOnly(diff.formattingOnly);
  }, [editor, markdown, baseline, diffLabels]);

  return (
    <Flex vertical className={clsx(styles.root, className)}>
      {diffLabels && formattingOnly && (
        <Text type="secondary" size="sm">
          {diffLabels.formattingChanged}
        </Text>
      )}
      <EditorContent editor={editor} />
    </Flex>
  );
}
